import Backbone from 'backbone'
import $ from 'jquery'
import _ from 'lodash'
import config from 'scoreboard/components/config'
import permissions from 'scoreboard/components/permissions'
import appDisplay from 'scoreboard/components/app-display'
import ErrorView from 'scoreboard/components/error-view'
import errorHandler from 'scoreboard/components/error-handler'
import Project from './project'
import Organization from 'scoreboard/organizations/organization'

// The project we're currently working within. This matches user preferences
// stored in the backend.
let projectId
// The project which is currently selected from the project menu but has not yet
// been switched over to. This is managed in the menu-manager and exposed here
// referencing around the app.
let pendingProjectId

/**
 * A map so order is guaranteed
 * @type {Map<Number, Project>}
 * @private
 */
const projectsById = new Map()

var projects = {

	/**
	 * @return {Number} The id of the active project
	 */
	get id() {
		return projectId
	},

	get pendingProjectId() {
		return pendingProjectId
	},

	/**
	 * @return {Number} The root orgId for the active project
	 */
	get orgId() {
		return _.get(current(), 'attributes.orgId', '')
	},

	/**
	 * @return {String} The label of the active project
	 */
	get label() {
		return _.get(current(), 'attributes.label', '')
	},

	/**
	 * Get a copy of the project list as a plain objects
	 * @return {Object[]} with all project attributes
	 */
	asArray() {
		const copy = []
		projectsById.forEach(p => copy.push(_.clone(p.attributes)))
		return copy
	},

	/**
	 * Get a project by it's id
	 * @param {Number|String} id
	 * @return {Project}
	 */
	get(id) {
		return projectsById.get(_.toNumber(id))
	},

	/**
	 * Set the active project
	 * @param {Number|String} id the project id the user is viewing
	 */
	set id(id) {
		const numericId = _.toNumber(id)
		if (this.get(numericId)) {
			if (projectId !== numericId) {
				projectId = numericId
				Backbone.trigger('change:project', projectId)
			}
		} else {
			projectId = undefined
		}
	},

	set pendingProjectId(id) {
		pendingProjectId = id
	},

	/**
	 * If someone only has permission to view one project
	 * and they don't have the ability to edit projects,
	 * then they shouldn't even see the project selector.
	 */
	isVisible() {
		return !!config.enableProjects && (!this.single() || permissions.modifyProjects())
	},

	/**
	 * @return {boolean} true if only a single project is setup
	 */
	single() {
		return projectsById.size <= 1
	},

	/**
	 * @return {number} The number of projects the user can see.
	 * This is derived from the orgs they have permission to view.
	 * So, if the user has no projects its means they also have no org permissions.
	 */
	size() {
		return projectsById.size
	},

	/**
	 * In a situation where projectId is mandatory this will return the selected
	 * project's id, or if none is selected the first in the list.
	 *
	 * (e.g. Displaying an org tree when a project has not yet been chosen)
	 */
	selectedOrFirstId() {
		return projectId || this.firstProjectId()
	},

	/**
	 * project id of the first project in the list
	 */
	firstProjectId() {
		if (!projectsById.size) {
			return
		}
		return projectsById.values().next().value.id
	},

	/**
	 * Give an array of objects, transform into Project Model's and store them
	 * in global state.
	 *
	 * @param {Object[]} projectNodes {id, label, orgId, order}
	 *
	 * @param {String|Number} [newProjectId] will replace projectId if provided,
	 * 	otherwise a single project will be chosen, if multiple projects exist
	 * 	and the previous projectId no longer exists in the list of projects
	 * 	it will be unset
	 */
	update(projectNodes, newProjectId) {
		projectsById.clear()
		_.each(projectNodes, p => set(new Project(p)))

		// Assign whatever the user clicked, or their persisted selection
		if (newProjectId) {
			projects.id = newProjectId
		}

		// User could have been removed from the project in their preference
		if (!this.get(projectId)) {
			projects.id = undefined
		}
		// If there is only one project it must be auto-selected because these
		// users will not be shown the project selector
		if (projectsById.size === 1) {
			projects.id = projects.firstProjectId()
		}
	},

	/**
	 * When a new project selection is made we have to update the user's preference.
	 * This method issues the POST and handles a permissions exception if they no longer
	 * have access to that project.
	 */
	postActiveProject() {
		const errorHandlerFn = xhr => errorHandler(xhr, { handler: () => {
			const response = xhr.responseJSON
			const {messageCode, projects} = response
			if (projects) {
				// These are the projects the user CAN see
				this.update(projects)
				Backbone.trigger('projects', projects)
				appDisplay.dialog(new ErrorView({
					title: 'common.security.accessDenied',
					messageCode
				}))
				return true
			}
		}})

		// Inform the server of the change, so user preference can be saved
		return $.ajax({
			method: 'post',
			url: `api/projects/${projectId}/active`,
			error: errorHandlerFn
		})
	},

	/**
	 * Returns the currently active project if there is one
	 */
	current() {
		return current()
	},

	pending() {
		return this.get(pendingProjectId)
	}
}

// Private

function current() {
	return projectsById.get(projectId)
}

function set(project) {
	projectsById.set(project.id, project)
}

function remove(project) {
	projectsById.delete(project.id)
}


function interestedAction(func, model) {
	return model instanceof Project && func(model)
}

function onAddDomainObject(func, model) {
	// If a project was just added, add it to projectsById
	if (model instanceof Project) {
		return set(model)
	}

	// If an org was just added, check to see of that org was saved as the first
	// org to a newly created project, we should update that project so that it's
	// orgId attribute reflects this root org.
	let pending = projectsById.get(pendingProjectId)
	if (model instanceof Organization && pending && _.isNil(pending.get('orgId')))  {
		pending.attributes.orgId = model.id
	}
}

Backbone.on('load', ({projectId, projects: data}) => projects.update(data, projectId))
Backbone.on('add:domain-object', onAddDomainObject.bind(projects, set))
Backbone.on('edit:domain-object', interestedAction.bind(projects, set))
Backbone.on('delete:domain-object', interestedAction.bind(projects, remove))

const LOGO = 'resources/images3/project.svg'

// For testing
/**
 * PRIVATE - Allows tests to mutate projects without Backbone events
 * @type {Map<Number, Project>}
 */

const _projectMap = projectsById

_.extend(projects, {
	LOGO,
	_projectMap
})
export default projects
export { LOGO, _projectMap }
projects.__filename__ = 'projects/projects.js'
